import React, { useEffect, useMemo, useState } from 'react';

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

export default function MonthlyInstalledMetersCard({
  title = 'Monthly Installed Meters',
  labels = [],
  values = [],
}) {
  /** Only set when user clicks a data point; null = no value / no tooltip */
  const [selectedIdx, setSelectedIdx] = useState(null);

  const width = 360;
  const height = 220;
  /** Plot insets inside the viewBox (tighter = less empty space around the series) */
  const paddingLeft = 28;
  const paddingRight = 8;
  const paddingTop = 8;
  const paddingBottom = 22;

  /**
   * Single source of truth for scale + path + dots (must match exactly).
   * Previously the path used uniform padding:18 while dots used asymmetric padding — that caused the line/dots offset.
   */
  const chart = useMemo(() => {
    const safeValues = values?.length ? values : [];
    const safeLabels = safeValues.map((_, i) =>
      labels[i] != null && labels[i] !== '' ? String(labels[i]) : String(i + 1)
    );

    const innerW = width - paddingLeft - paddingRight;
    const innerH = height - paddingTop - paddingBottom;

    if (safeValues.length === 0) {
      return {
        points: [],
        pathD: '',
        minV: 0,
        maxV: 1,
        yTicks: [0, 1],
        xTickIndices: [],
      };
    }

    let minV = Math.min(0, ...safeValues);
    let maxV = Math.max(1, ...safeValues);
    if (maxV <= minV) {
      maxV = minV + 1;
    }
    const span = maxV - minV;

    const xFor = (i) =>
      paddingLeft + innerW * (safeValues.length <= 1 ? 0 : i / (safeValues.length - 1));
    const yFor = (v) => paddingTop + innerH - innerH * ((v - minV) / span);

    const points = safeValues.map((v, i) => ({
      i,
      v,
      label: safeLabels[i] ?? '',
      x: xFor(i),
      y: yFor(v),
    }));

    const pathD = points
      .map((p, idx) => `${idx === 0 ? 'M' : 'L'} ${p.x.toFixed(2)} ${p.y.toFixed(2)}`)
      .join(' ');

    const tickCount = 4;
    const yTicks = Array.from({ length: tickCount + 1 }, (_, j) => {
      const t = j / tickCount;
      return Math.round(minV + span * (1 - t));
    });

    const n = safeValues.length;
    let xTickIndices;
    if (n <= 12) {
      xTickIndices = safeValues.map((_, i) => i);
    } else {
      const step = Math.max(1, Math.round(n / 6));
      xTickIndices = safeValues
        .map((_, i) => i)
        .filter((i) => i === 0 || i === n - 1 || i % step === 0);
    }

    return {
      points,
      pathD,
      minV,
      maxV,
      yTicks,
      xTickIndices,
      safeLabels,
    };
  }, [values, labels, width, height, paddingLeft, paddingRight, paddingTop, paddingBottom]);

  useEffect(() => {
    setSelectedIdx((idx) => {
      if (idx == null) return null;
      return idx < chart.points.length ? idx : null;
    });
  }, [chart.points.length]);

  const selectedPoint = selectedIdx != null ? chart.points[selectedIdx] : null;

  return (
    <div className="dash-card dash-card--monthly-chart">
      <div className="dash-card-head">
        <h3 className="dash-card-title">{title}</h3>
      </div>

      <div className="mini-line-wrap" aria-label={title}>
        {/*
          Default SVG scaling uses preserveAspectRatio="meet", so a 360-wide viewBox stays ~360px
          wide in a full-width card and leaves large side gaps. "none" stretches the plot to the
          card width so the chart uses the horizontal space.
        */}
        <svg
          className="mini-line"
          width="100%"
          viewBox={`0 0 ${width} ${height}`}
          preserveAspectRatio="none"
          role="img"
          aria-label={title}
        >
          <rect
            x="0"
            y="0"
            width={width}
            height={height}
            fill="transparent"
            className="mini-line-hit"
            onClick={() => setSelectedIdx(null)}
          />

          {/* Horizontal grid + Y labels (same vertical range as the line) */}
          {chart.yTicks.map((tick, i) => {
            const y =
              paddingTop +
              ((height - paddingTop - paddingBottom) * (i / (chart.yTicks.length - 1)));
            return (
              <g key={i}>
                <line
                  x1={paddingLeft}
                  y1={y}
                  x2={width - paddingRight}
                  y2={y}
                  className="mini-line-grid"
                />
                <text x={paddingLeft - 10} y={y + 4} className="mini-line-axis" textAnchor="end">
                  {tick}
                </text>
              </g>
            );
          })}

          {/* Vertical grid at each labeled X tick (aligned with data points) */}
          {chart.xTickIndices.map((idx) => {
            const p = chart.points[idx];
            if (!p) return null;
            return (
              <line
                key={`vgrid-${idx}`}
                x1={p.x}
                y1={paddingTop}
                x2={p.x}
                y2={height - paddingBottom}
                className="mini-line-grid"
              />
            );
          })}

          {chart.xTickIndices.map((idx) => {
            const p = chart.points[idx];
            if (!p) return null;
            return (
              <text
                key={`xlab-${idx}`}
                x={p.x}
                y={height - 10}
                className="mini-line-axis"
                textAnchor="middle"
              >
                {p.label}
              </text>
            );
          })}

          {chart.pathD ? <path d={chart.pathD} className="mini-line-path" /> : null}

          {chart.points.map((p) => (
            <circle
              key={p.i}
              cx={clamp(p.x, paddingLeft, width - paddingRight)}
              cy={clamp(p.y, paddingTop, height - paddingBottom)}
              r={selectedIdx === p.i ? 5 : 4}
              className="mini-line-dot"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedIdx(p.i);
              }}
            />
          ))}

          {selectedPoint && (
            (() => {
              const tipW = 120;
              const tipH = 44;
              const x = clamp(
                selectedPoint.x - tipW / 2,
                paddingLeft,
                width - paddingRight - tipW
              );
              const y = clamp(
                selectedPoint.y + 12,
                paddingTop,
                height - paddingBottom - tipH
              );
              return (
                <g className="mini-line-tooltip" onClick={(e) => e.stopPropagation()}>
                  <rect x={x} y={y} width={tipW} height={tipH} rx="10" className="mini-line-tooltip-box" />
                  <text x={x + 12} y={y + 18} className="mini-line-tooltip-title">
                    {selectedPoint.label || '—'}
                  </text>
                  <circle cx={x + 16} cy={y + 32} r="4" className="mini-line-tooltip-dot" />
                  <text x={x + 28} y={y + 35} className="mini-line-tooltip-text">
                    Meters: {selectedPoint.v}
                  </text>
                </g>
              );
            })()
          )}
        </svg>

        <div className="mini-line-legend">
          <span className="mini-line-legend-dot" aria-hidden="true" />
          <span className="mini-line-legend-text">Meters Installed</span>
        </div>
      </div>
    </div>
  );
}
